import { useEffect, type RefObject } from "react";

/** Scroll invites a drawing; time completes it. Hold the finished ink throughout
 * the visible visit and re-arm only fully offscreen, in either direction.
 * One scheduler runs only on geometry changes or while ink is being drawn.
 */
export function useSectionEmphasis(root: RefObject<HTMLElement>) {
  useEffect(() => {
    const node = root.current;
    if (!node) return;
    const marks = [...node.querySelectorAll<HTMLElement>(".dv-emphasis")].map(element => ({
      element,
      state: "ready" as "ready" | "drawing" | "complete",
      startedAt: 0,
      progress: -1,
      duration: element.dataset.mark === "circle" || element.dataset.mark === "double" ? 1100 : 900,
    }));
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    const update = (now: number) => {
      frame = 0;
      const height = window.innerHeight;
      const atEnd = window.scrollY + height >= document.documentElement.scrollHeight - 2;
      // Read all geometry before writing styles, including both responsive titles.
      const positions = marks.map(mark => mark.element.getBoundingClientRect());
      let drawing = false;
      positions.forEach((rect, index) => {
        const mark = marks[index];
        const offscreen = !rect.height || rect.bottom <= 0 || rect.top >= height;
        // Wait until the word reaches the reader, not the bottom edge. The upper
        // bound admits sticky headings and re-entry below the floating navigation.
        const inReadingArea = rect.top >= 96 && rect.top + rect.height / 2 <= height * .64;
        const visibleAtEnd = atEnd && rect.top >= 96 && rect.bottom <= height;
        if (preference.matches || (document.hidden && mark.state === "drawing")) {
          mark.state = "complete";
        } else if (offscreen) {
          mark.state = "ready";
        } else if (mark.state === "ready" && (inReadingArea || visibleAtEnd)) {
          mark.state = "drawing";
          mark.startedAt = now;
        }

        let progress = mark.state === "complete" ? 1 : 0;
        if (mark.state === "drawing") {
          const time = Math.min(1, (now - mark.startedAt) / mark.duration);
          progress = time * time * (3 - 2 * time);
          if (time === 1) mark.state = "complete";
          else drawing = true;
        }
        mark.element.dataset.emphasisState = mark.state;
        if (progress !== mark.progress) {
          mark.element.style.setProperty("--dv-mark-progress", progress.toFixed(4));
          mark.progress = progress;
        }
      });
      if (drawing && !document.hidden) frame = requestAnimationFrame(update);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const resize = new ResizeObserver(schedule);
    resize.observe(node);
    marks.forEach(mark => resize.observe(mark.element));
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    preference.addEventListener("change", schedule);
    document.addEventListener("visibilitychange", schedule);
    update(performance.now());
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      preference.removeEventListener("change", schedule);
      document.removeEventListener("visibilitychange", schedule);
    };
  }, [root]);
}
